import httpClient from "../http/http-client";
import { INominalCodeImportPreview, INominalCodeMapping, INominalLedgerRefreshResult, INominalReapplyResult, INominalSummaryParams, INominalSummaryResponse, IPostNominalCodeMapping, NominalMappingSource } from './nominal-code-mapping.model';

export default class NominalCodeMappingService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAll(query?: string): Promise<INominalCodeMapping[]> {
    return this.http.get(`nominal-code-mapping${query ? `?${query}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getOne(id: string): Promise<INominalCodeMapping> {
    return this.http.get(`nominal-code-mapping/${id}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async create(mapping: IPostNominalCodeMapping): Promise<INominalCodeMapping> {
    return this.http.post(`nominal-code-mapping`, mapping)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async edit(id: string, mapping: IPostNominalCodeMapping): Promise<INominalCodeMapping> {
    return this.http.patch(`nominal-code-mapping/${id}`, mapping)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async delete(id: string): Promise<INominalCodeMapping> {
    return this.http.delete(`nominal-code-mapping/${id}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Save a batch of mappings in one call. `source` tells the telemetry which surface the
  // batch came from (the connect-time ledger wizard, the in-import step, the settings page)
  // so the wizard's completion rate is readable; it is an enum, never free text.
  async bulkUpsert(
    mappings: IPostNominalCodeMapping[],
    options?: { source?: NominalMappingSource, provider?: string },
  ): Promise<{ results: unknown[], reapply?: INominalReapplyResult | null, reapplyMessage?: string | null }> {
    return this.http.post(`nominal-code-mapping/bulk`, { mappings, ...options })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Apply the saved crosswalk to connected-ledger lines already waiting in review.
  // Spreadsheet imports are NOT covered here: they re-resolve through the file upload's own
  // validation re-run, which runs the full check ladder including the nominal step.
  async reapplyToPendingLines(reportPeriodId?: string): Promise<INominalReapplyResult> {
    return this.http.post(`nominal-code-mapping/reapply`, { reportPeriodId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // The chart-of-accounts template: columns to fill in, plus a Reference tab listing every
  // Outcome, Emission Group and Spend Activity this account accepts.
  async getImportTemplate(): Promise<any> {
    return this.http.get(`nominal-code-mapping/import/template`, { responseType: 'blob' })
      .then(response => {
        return response
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Parse an uploaded chart of accounts and return what it would do. Writes nothing: the caller
  // posts the valid rows to bulkUpsert once the user has reviewed them.
  async previewImport(file: File): Promise<INominalCodeImportPreview> {
    const form = new FormData();
    form.append('files', file);
    return this.http.post(`nominal-code-mapping/import/preview`, form)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Every code the organisation has mapped or seen, with spend totals, the saved mapping and any
  // suggestion: everything the Chart of Accounts page needs in one call. The ledger's chart comes
  // from the stored copy, so no param is needed for it. `reportPeriodId` restricts the ledger-line
  // totals to one period, `fileUploadId` the spreadsheet totals to one upload. Empty params are
  // left off rather than sent blank.
  async getSummary(params: INominalSummaryParams = {}): Promise<INominalSummaryResponse> {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') query.append(key, value.trim())
    })
    const queryString = query.toString()
    return this.http.get(`nominal-code-mapping/summary${queryString ? `?${queryString}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Save the rows of an imported chart of accounts that carry no Outcome, as accounts with no
  // outcome yet. Rows with an outcome still go through bulkUpsert.
  async importLedgerAccounts(accounts: { nominalCode: string, nominalDescription: string | null }[]): Promise<{ accountCount: number, created: number, updated: number }> {
    return this.http.post(`nominal-code-mapping/ledger-accounts/import`, { accounts })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Read the chart of accounts from a connected ledger now and store it. Every sync does the same;
  // this is the page's "Read from your ledger" / "Refresh from {ledger}". A failed read rejects
  // with the backend's message for the user in `response.data.message`.
  async refreshLedgerAccounts(params: { provider: string, companyId?: string | null }): Promise<INominalLedgerRefreshResult> {
    return this.http.post(`nominal-code-mapping/ledger-accounts/refresh`, {
      provider: params.provider,
      companyId: params.companyId || null,
    })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }
}
